import React, { useState, useEffect, useRef } from 'react';
import { oneOfType, array, object } from 'prop-types'
import { withRouter } from 'react-router-dom';
import firebaseApp from 'firebase/app';
import { Modal, notification, Divider } from 'antd'
import { withApollo } from 'react-apollo';
import { map, get, orderBy, flatten, includes, uniq, size, filter } from 'lodash';
import { Button, FlexContainer, Main, Row, FormInput, Column, Accordion, Text, FormSelect, IconContainer, Icon } from '../../common';
import { withFirebase } from '../../../hoc';
import Settings from './GlobalSettings';
import { GREEN_DARK } from '../../../utils/colors';

const styleNotification = {
    borderRadius: 8,
    height: 42,
    width: 500,
    padding: '9px 12px',
    marginLeft: 384 - 500,
    top: 15
};

const SCSettings = ({ firebase }) => {
    const [storeSettings, setStoreSettings] = useState(null);
    const storeSettingsRef = useRef(null);
    const [unmappedConfigs, setUnmappedConfigs] = useState([]);
    const [typeParam, setTypeParam] = useState(null);
    const [nameParam, setNameParam] = useState(null);
    const [descriptionParam, setDescriptionParam] = useState(null);
    const [description, setDescription] = useState(null);
    const [category, setCategory] = useState(null);
    const [textPlaceholder, setTextPlaceholder] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [key, setKey] = useState(null);

    const showNotification = (isSuccess, isDelete, param) => {
        const error = {
            msg: `Hubo un error al ${isDelete ? 'eliminar' : 'actualizar'} <strong>${param}</strong>.`,
            color: '#BD2C00',
            icon: 'alert-circle',
            bgColor: '#F7E6E1',
            borderColor: '#BD2C00'
        };

        const success = {
            msg: `<strong>${param}</strong> se ${isDelete ? 'eliminó' : 'actualizó'} correctamente.`,
            color: '#22A889',
            icon: 'checkmark-circle',
            bgColor: '#E6F5F1',
            borderColor: '#B6DFD6'
        };

        const { msg, bgColor, icon, color, borderColor } = isSuccess ? success : error;

        notification.open({
            message: (<Row align='center'>
                <Icon name={icon} size={24} color={color} />
                <Divider type='vertical' style={{ background: color, height: '1.5em', margin: '0px 16px 0px 14px' }} />
                <Text text={<div dangerouslySetInnerHTML={{ __html: msg }} />} size={12} color='#323A44' />
            </Row>),
            style: { ...styleNotification, backgroundColor: bgColor, border: `1px solid ${borderColor}` }
        });
    };

    const typeParamsOptions = [
        { label: 'Lista', value: 'object' },
        { label: 'Texto', value: 'string' },
        { label: 'Switch', value: 'bool' },
        { label: 'Número', value: 'number' },
    ];

    const categoryOptions = map(storeSettings, ({ description }) => ({ label: description, value: description }));

    useEffect(() => {
        storeSettingsRef.current = storeSettings;
    }, [storeSettings]);

    const processConfigs = async () => {
        const currentStoreSettings = storeSettingsRef.current;
        if (!currentStoreSettings) return;

        const storesSnapshot = await firebase.collection('salesChannel').get();
        const globalSettings = flatten(map(currentStoreSettings, 'settings'));

        const allUnmappedConfigsByStore = [];

        for (const storeDoc of storesSnapshot.docs) {
            const storeData = storeDoc.data();
            const storeConfigs = Object.keys(storeData);
            const invalidConfigs = storeConfigs.filter(config => {
                return !includes(map(globalSettings, 'key'), config);
            });

            allUnmappedConfigsByStore.push(invalidConfigs);
        }

        const allUnmappedConfigs = uniq(flatten(allUnmappedConfigsByStore));
        setUnmappedConfigs(allUnmappedConfigs);
    };

    const removeSettingFromElement = async (key) => {
        const docRef = firebase.collection('config').doc('backoffice');
    
        try {
            const doc = await docRef.get();
            const data = doc.data();
    
            const updatedStoreSettings = map(get(data, 'store-settings'), item => {
                if (get(item, 'description') === description) {
                    return {
                        ...item,
                        settings: filter(get(item, 'settings'), setting => get(setting, 'key') !== key)
                    };
                }
                return item;
            });
    
            await docRef.update({
                'store-settings': updatedStoreSettings
            });
        } catch (error) {
            console.error('Error al eliminar el setting:', error);
        }
    };

    const addSettingToElement = async () => {
        const docRef = firebase.collection('config').doc('backoffice');

        try {
            const doc = await docRef.get();
            const data = doc.data();

            const updatedStoreSettings = map(get(data, 'store-settings'), item => {
                if (get(item, 'description') === get(category, 'value')) {
                    return {
                        ...item,
                        settings: [
                            ...item.settings,
                            {
                                key: nameParam,
                                placeholder: textPlaceholder,
                                order: size(get(item, 'settings')) + 1,
                                value: descriptionParam
                            }
                        ]
                    };
                }
                return item;
            });

            await docRef.update({
                'store-settings': updatedStoreSettings
            });
        } catch (error) {
            console.error('Error al actualizar los settings:', error);
        }
    };
    
    useEffect(() => {
        const unsubscribeConfig = firebase.collection('config')
            .doc('backoffice')
            .onSnapshot((doc) => {
                const storeSettingsData = get(doc.data(), 'store-settings', []);
                setStoreSettings(storeSettingsData);
                processConfigs();
            });

        const unsubscribeSalesChannel = firebase.collection('salesChannel')
            .onSnapshot(() => {
                processConfigs();
            });

        return () => {
            unsubscribeConfig();
            unsubscribeSalesChannel();
        };
    }, []);

    const getValueByType = () => {
        const typeValue = get(typeParam, 'value')
        if (typeValue === 'object') {
            return [];
        }

        if (typeValue === 'bool') {
            return false;
        }

        if (typeValue === 'number') {
            return 0;
        }

        return '';
    }

    const updateStoreSettings = async () => {
        setLoading(true);

        try {
            const collectionRef = firebase.collection('salesChannel');
            const querySnapshot = await collectionRef.get();

            const updatePromises = querySnapshot.docs.map(async (doc) => {
                const docRef = doc.ref;
                const newField = { [nameParam]: getValueByType() };

                return docRef.update(newField);
            });
            
            await Promise.all(updatePromises);
            showNotification(true, false, nameParam);
            processConfigs(storeSettings);
            setLoading(false);
            addSettingToElement();
            console.log('Todos los campos han sido actualizados');
        } catch (error) {
            showNotification(true, false, nameParam);
            console.error('Error al actualizar campos: ', error);
        }
    }

    const deleteStoreSetting = async (key) => {
        setLoading(true);
        const collectionRef = firebase.collection('salesChannel');
        
        try {
            const querySnapshot = await collectionRef.get();
            
            const deletePromises = querySnapshot.docs.map(async (doc) => {
                const docRef = doc.ref;
                
                return docRef.update({
                    [key]: firebaseApp.firestore.FieldValue.delete()
                });
            });
            
            await Promise.all(deletePromises);
            processConfigs(storeSettings);
            setLoading(false);
            handleModal(null);
            showNotification(true, true, key);
            removeSettingFromElement(key)
            console.log('Todos los campos han sido eliminados');
        } catch (error) {
            showNotification(true, true, key);
            console.error('Error al eliminar campos: ', error);
        }
    }

    const onChangeTypeParam = ({ value }) => {
        setTypeParam(value);
    };

    const onChangeNameParam = (value) => {
        setNameParam(value);
    };

    const onChangeCategory = ({ value }) => {
        setCategory(value);
    };

    const onChangeDescriptionParam = (value) => {
        setDescriptionParam(value);
    };

    const onChangeTextPlaceholder = (value) => {
        setTextPlaceholder(value);
    };

    const handleModal = (key, description) => {
        setKey(key);
        setDescription(description)
        setModalVisible(!modalVisible);
    }

    const unmappedConfigsFormatted = map(unmappedConfigs, (value) => ({ key: value, value }));

    const modal = () => {
        return <Modal
            visible={modalVisible}
            onCancel={() => handleModal(null)}
            footer={null}
        >
            <Column align='center'>
                <IconContainer>
                    <Icon name='trash' color='#ffffff' size={46.8} />
                </IconContainer>
                <FlexContainer mY={30} direction='column' align='center'>
                    <Row justify='center'>
                        <Text text='¿Estas seguro de eliminar este parámetro&nbsp;' />
                        <Text text={key} strong />
                    </Row>
                </FlexContainer>
                <FlexContainer justify='flex-end' style={{ width: '100%' }}>
                    <Button link text='btn.action.cancel' style={{ width: 150 }} onClick={() => handleModal(null)} />
                    <Button loading={loading} text='btn.action.delete' style={{ width: 150 }} onClick={() => deleteStoreSetting(key)} />
                </FlexContainer>
            </Column>
        </Modal>
    }

    return (
        <Main background='f4f4f4' withFooter>
            <FlexContainer padding={23}>
                <Column
                    border='1px solid #eaeaea'
                    borderRadius={3}
                    paddingVertical={10}
                    paddingHorizontal={13}
                    noWrap
                    backgroundColor='white'
                >
                    <Text text={'Actualizar todas las salas'} strong color={GREEN_DARK} size={16} />
                    <Row gap={5}>
                        <FormSelect
                            style={{ flex: 1 }}
                            label='Categoria'
                            value={category}
                            options={categoryOptions}
                            onChange={onChangeCategory}
                        />
                        <FormInput
                            style={{ flex: 1 }}
                            label={'Nombre del parámetro'}
                            placeholder='Ingrese nombre'
                            onChange={({ target }) => onChangeNameParam(get(target, 'value'))}
                            value={nameParam}
                        />
                        <FormInput
                            style={{ flex: 1 }}
                            label={'Descripción'}
                            placeholder='Ingrese descripción'
                            onChange={({ target }) => onChangeDescriptionParam(get(target, 'value'))}
                            value={descriptionParam}
                        />
                    </Row>
                    <Row gap={5}>
                        <FormSelect
                            style={{ flex: 1 }}
                            label='Tipo'
                            value={typeParam}
                            options={typeParamsOptions}
                            onChange={onChangeTypeParam}
                        />
                        {!includes(['string', 'object', 'number'], get(typeParam, 'value'))
                            ? <div style={{ flex: 1 }}></div>
                            : <FormInput
                                style={{ flex: 1 }}
                                label={'Text para placeholder'}
                                placeholder='Ingrese texto'
                                onChange={({ target }) => onChangeTextPlaceholder(get(target, 'value'))}
                                value={textPlaceholder}
                            />
                        }
                        <div style={{ flex: 1 }}></div>
                    </Row>
                    <Row justify='flex-end' pB={5}>
                        <Button loading={loading} text='Agregar parámetro' width={200} onClick={() => updateStoreSettings()} />
                    </Row>
                </Column>
            </FlexContainer>
            <FlexContainer paddingHorizontal={23}>
                <Column>
                    <Text text={'Configuraciones sin mapear'} strong color={GREEN_DARK} paddingVertical={10} size={16} />
                    <Accordion
                        iconOpened='chebron_down'
                        iconClosed='chebron_up'
                        iconSize={9}
                        title={<Text text={'Sin categoria'} strong />}
                        styleHeader={{ borderRadius: 8 }}
                        style={{ borderRadius: 8, marginBottom: 5, boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)', padding: 3 }}
                    >
                        <Divider style={{ padding: 0, margin: 0 }} />
                        <Column mB={15}>
                            <Settings
                                settings={unmappedConfigsFormatted}
                                deleteStoreSetting={deleteStoreSetting}
                                handleModal={handleModal}
                            />
                        </Column> 
                    </Accordion>
                    <Text text={'Configuraciones mapeadas'} strong color={GREEN_DARK} paddingVertical={10} size={16} />
                    {map(orderBy(storeSettings, 'order', ['asc']), ({ description, settings }, index) => (
                        <Accordion
                            key={index}
                            iconOpened='chebron_down'
                            iconClosed='chebron_up'
                            iconSize={9}
                            title={<Text text={description} strong />}
                            styleHeader={{ borderRadius: 8 }}
                            style={{ borderRadius: 8, marginBottom: 5, boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.08)', padding: 3 }}
                        >
                            <Divider style={{ padding: 0, margin: 0 }} />
                            <Column mB={15}>
                                <Settings
                                    settings={settings}
                                    description={description}
                                    deleteStoreSetting={deleteStoreSetting}
                                    handleModal={handleModal}
                                />
                            </Column> 
                        </Accordion>
                    ))}
                </Column>
            </FlexContainer>
            {modal()}
        </Main>
    );
};

SCSettings.propTypes = {
    firebase: oneOfType([array, object]),
    session: object,
    client: object
};

export default withApollo(withFirebase(withRouter(SCSettings)));
